import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';

import UserAvatar from '../users/UserAvatar';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useOptionalPreferences } from '../../contexts/PreferencesContext';
import {
	HOME_TITLE_MAX,
	homeGreeting,
	homeZone,
	msUntilGreetingChanges,
	readHomeTitle,
} from '../../utils/homeGreeting';

const AVATAR_PX = 32;

/**
 * What Home's title says (KPR-1122): the person's own title when they wrote
 * one, else the time-of-day greeting. The greeting re-reads the clock when
 * its period ends, so a page left open changes on its own.
 */
export function useHomeTitle() {
	const { user } = useCurrentUser();
	const prefsContext = useOptionalPreferences();
	const prefs = prefsContext?.prefs || {};
	const zone = homeZone(prefs.timezone);
	const [now, setNow] = useState(() => new Date());

	useEffect(() => {
		const timer = setTimeout(() => setNow(new Date()), msUntilGreetingChanges(now, zone) + 250);
		return () => clearTimeout(timer);
	}, [now, zone]);

	const custom = typeof prefs.homeTitle === 'string' ? prefs.homeTitle.trim() : '';
	return {
		user,
		custom,
		text: custom || homeGreeting({ now, zone, firstName: user?.firstName }),
	};
}

/**
 * The Home header's title: the person's avatar (photo, else initials) then
 * the text, on one line that truncates the text before it wraps.
 */
export default function HomeHeading() {
	const { user, text } = useHomeTitle();
	return (
		<span className="home-heading">
			{user ? (
				<span className="home-heading__avatar" aria-hidden="true" data-testid="home-heading-avatar">
					<UserAvatar user={user} size={AVATAR_PX} />
				</span>
			) : null}
			<span className="home-heading__text" title={text}>{text}</span>
		</span>
	);
}

/** "Edit title" from the header's ⋯: the same preference Preferences edits. */
export function EditHomeTitleModal({ open, onClose }) {
	const prefsContext = useOptionalPreferences();
	const stored = typeof prefsContext?.prefs?.homeTitle === 'string' ? prefsContext.prefs.homeTitle : '';
	const [text, setText] = useState(stored);
	const [saving, setSaving] = useState(false);
	const parsed = readHomeTitle(text);

	useEffect(() => { if (open) setText(stored); }, [open, stored]);

	const save = async () => {
		if (parsed.error || !prefsContext) return;
		setSaving(true);
		const ok = await prefsContext.setPref('homeTitle', parsed.value);
		setSaving(false);
		if (ok) onClose();
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Edit title"
			centered
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose}>Cancel</Button>,
				<Button key="save" type="primary" loading={saving} disabled={Boolean(parsed.error)} onClick={save}>Save title</Button>,
			]}
		>
			<Form layout="vertical" onFinish={save}>
				<Form.Item
					label="Title"
					extra="Leave it empty to be greeted by the time of day."
					validateStatus={parsed.error ? 'error' : undefined}
					help={parsed.error || undefined}
				>
					<Input
						autoFocus
						value={text}
						onChange={event => setText(event.target.value)}
						onPressEnter={save}
						placeholder="Good morning"
						maxLength={HOME_TITLE_MAX + 20}
						aria-label="Title"
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}
